import React, { useMemo } from "react";
import { summarize } from "../utils";

const num = (v: any, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

function pickPanelStats(status: any) {
  const s1h = status?.stats?.home;
  const s1a = status?.stats?.away;

  const s2h = status?.c?.stats?.home ?? status?.c?.stats?.h;
  const s2a = status?.c?.stats?.away ?? status?.c?.stats?.a;

  const s3h = status?.st?.h;
  const s3a = status?.st?.a;

  const readXg = (obj: any) =>
    num(
      obj?.xg ??
        obj?.xG ??
        obj?.xg_total ??
        obj?.xgtotal ??
        obj?.total_xg ??
        obj?.xgT
    );

  const readShots = (obj: any) =>
    num(obj?.shots ?? obj?.shots_total ?? obj?.total_shots ?? obj?.sh);

  const readOnTarget = (obj: any) =>
    num(
      obj?.onTarget ??
        obj?.on_target ??
        obj?.shots_on_target ??
        obj?.sot ??
        obj?.onT
    );

  const firstNonNull = (...arr: any[]) => arr.find((x) => x != null);

  const H =
    firstNonNull(s1h, s2h, s3h, status?.sxg?.h, status?.c?.sxg?.h) ?? {};
  const A =
    firstNonNull(s1a, s2a, s3a, status?.sxg?.a, status?.c?.sxg?.a) ?? {};

  const totalXgH =
    readXg(H) ||
    num(status?.sxg?.h) ||
    num(status?.c?.sxg?.h) ||
    num(status?.xgTotals?.home);
  const totalXgA =
    readXg(A) ||
    num(status?.sxg?.a) ||
    num(status?.c?.sxg?.a) ||
    num(status?.xgTotals?.away);

  const shotsH =
    readShots(H) ||
    num(status?.shots?.h) ||
    num(status?.c?.shots?.h) ||
    num(status?.stats_simple?.home?.shots);
  const shotsA =
    readShots(A) ||
    num(status?.shots?.a) ||
    num(status?.c?.shots?.a) ||
    num(status?.stats_simple?.away?.shots);

  const onTargetH =
    readOnTarget(H) ||
    num(status?.onTarget?.h) ||
    num(status?.c?.onTarget?.h) ||
    num(status?.stats_simple?.home?.onTarget);
  const onTargetA =
    readOnTarget(A) ||
    num(status?.onTarget?.a) ||
    num(status?.c?.onTarget?.a) ||
    num(status?.stats_simple?.away?.onTarget);

  const hasAny =
    totalXgH > 0 ||
    totalXgA > 0 ||
    shotsH > 0 ||
    shotsA > 0 ||
    onTargetH > 0 ||
    onTargetA > 0;

  return hasAny
    ? {
        h: { totalXg: totalXgH, shotsCount: shotsH, onTarget: onTargetH },
        a: { totalXg: totalXgA, shotsCount: shotsA, onTarget: onTargetA },
      }
    : null;
}

type StatsPanelProps = {
  status: any;
  sumH?: ReturnType<typeof summarize>;
  sumA?: ReturnType<typeof summarize>;
};

const StatsPanel: React.FC<StatsPanelProps> = ({ status, sumH, sumA }) => {
  const fromApi = useMemo(() => pickPanelStats(status), [status]);

  const left = fromApi
    ? {
        xg: fromApi.h.totalXg.toFixed(2),
        shots: String(fromApi.h.shotsCount),
        on: String(fromApi.h.onTarget),
      }
    : {
        xg: (sumH?.totalXg ?? 0).toFixed(2),
        shots: String(sumH?.shotsCount ?? 0),
        on: String(sumH?.onTarget ?? 0),
      };

  const right = fromApi
    ? {
        xg: fromApi.a.totalXg.toFixed(2),
        shots: String(fromApi.a.shotsCount),
        on: String(fromApi.a.onTarget),
      }
    : {
        xg: (sumA?.totalXg ?? 0).toFixed(2),
        shots: String(sumA?.shotsCount ?? 0),
        on: String(sumA?.onTarget ?? 0),
      };

  const rows = [
    { label: "Total Team xG", left: left.xg, right: right.xg },
    { label: "Shots", left: left.shots, right: right.shots },
    { label: "Shots on Target", left: left.on, right: right.on },
  ];

  return (
    <div className="mb-[1vw] rounded-[0.5vw] border px-[1vw] text-white border-border bg-darkestBlue">
      {rows.map((row, i) => (
        <div
          key={row.label}
          className={`grid grid-cols-3 items-center py-[1vw] border-b border-border last:border-b-0`}
        >
          <div
            className="text-left text-[clamp(12px,1.6vw,16px)] font-extrabold"
            style={{ color: "#fff" }}
          >
            {row.left}
          </div>
          <div className="text-center text-[clamp(12px,1.6vw,16px)] font-semibold opacity-75">
            {row.label}
          </div>
          <div
            className="text-right text-[clamp(12px,1.6vw,16px)] font-extrabold"
            style={{ color: "#fff" }}
          >
            {row.right}
          </div>
        </div>
      ))}
    </div>
  );
};

export default StatsPanel;
